import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MenuDraft, MenuItem, NewMenuItem } from "@glowin/schemas";
import { api, ApiError } from "../api.js";
import { displayName, formatMoney } from "../format.js";
import { ItemEditor } from "./ItemEditor.js";
import { PublishDialog } from "./PublishDialog.js";
import { toNewItem, toUpdatedItem } from "../item-form.js";
import { usePageTitle } from "../ui.js";
import {
  BanIcon,
  CheckCircleIcon,
  CheckIcon,
  MenuIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  SendIcon,
  WithIcon
} from "../icons.js";

/**
 * The daily-driver screen: browse the menu, edit an item, flip sold-out, publish.
 * Edits go to the DRAFT; nothing reaches a live call until Publish.
 */
export function MenuPage() {
  usePageTitle("Menu");
  const { restaurantId = "" } = useParams();
  const qc = useQueryClient();
  const draftKey = ["draft", restaurantId];
  const versionsKey = ["versions", restaurantId];

  // "new" opens a blank editor; a MenuItem opens it on that dish.
  const [editing, setEditing] = useState<MenuItem | "new" | null>(null);
  const [filter, setFilter] = useState("");
  const [confirmingPublish, setConfirmingPublish] = useState(false);

  const draft = useQuery({ queryKey: draftKey, queryFn: () => api.getDraft(restaurantId) });
  const versions = useQuery({ queryKey: versionsKey, queryFn: () => api.listVersions(restaurantId) });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: draftKey });
    void qc.invalidateQueries({ queryKey: versionsKey });
  };

  const setAvailability = useMutation({
    mutationFn: ({ itemId, available }: { itemId: string; available: boolean }) =>
      api.setAvailability(restaurantId, itemId, available),
    onSuccess: invalidate
  });

  const saveItem = useMutation({
    mutationFn: (item: MenuItem) => api.putItem(restaurantId, item),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    }
  });

  const addItem = useMutation({
    mutationFn: (item: NewMenuItem) => api.createItem(restaurantId, item),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    }
  });

  const removeItem = useMutation({
    mutationFn: (itemId: string) => api.deleteItem(restaurantId, itemId),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    }
  });

  const publish = useMutation({
    mutationFn: () => api.publish(restaurantId),
    onSuccess: () => {
      setConfirmingPublish(false);
      invalidate();
      void qc.invalidateQueries({ queryKey: ["publish-preview", restaurantId] });
    }
  });

  const menu: MenuDraft = draft.data ?? { categories: [], items: [], modifierGroups: [] };

  const grouped = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const match = (i: MenuItem) =>
      !q ||
      i.name.en.toLowerCase().includes(q) ||
      (i.name.zh ?? "").includes(filter.trim()) ||
      i.itemId.includes(q);

    const cats = [...menu.categories].sort((a, b) => a.sortIndex - b.sortIndex);
    const known = new Set(cats.map((c) => c.id));
    const sections = cats.map((c) => ({
      id: c.id,
      label: displayName(c.name),
      items: menu.items
        .filter((i) => i.categoryId === c.id && match(i))
        .sort((a, b) => a.sortIndex - b.sortIndex)
    }));
    const orphans = menu.items.filter((i) => !known.has(i.categoryId) && match(i));
    if (orphans.length > 0) sections.push({ id: "__orphans", label: "Uncategorized", items: orphans });
    return sections.filter((s) => s.items.length > 0);
  }, [menu, filter]);

  if (draft.isLoading) return <p className="muted">Loading menu…</p>;
  if (draft.error) return <ErrorBox error={draft.error} />;

  const soldOut = menu.items.filter((i) => !i.available).length;
  const liveVersion = versions.data?.liveVersion ?? 0;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Menu</h1>
          <p className="muted facts-line">
            <WithIcon icon={<MenuIcon size={15} />}>{menu.items.length} items</WithIcon>
            {soldOut > 0 ? <WithIcon icon={<BanIcon size={15} />}>{soldOut} sold out</WithIcon> : null}
            <WithIcon icon={<SendIcon size={15} />}>{liveVersion > 0 ? `Live v${liveVersion}` : "Not published"}</WithIcon>
          </p>
        </div>
        <div className="page-actions">
          <input
            className="search"
            placeholder="Search"
            aria-label="Search dishes"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <button
            className="secondary"
            onClick={() => setEditing("new")}
            disabled={menu.categories.length === 0}
            title={
              menu.categories.length === 0
                ? "Create a category first"
                : "Add a dish to the menu"
            }
          >
            <WithIcon icon={<PlusIcon size={16} />}>Add dish</WithIcon>
          </button>
          <button
            className="primary"
            onClick={() => setConfirmingPublish(true)}
            disabled={publish.isPending}
          >
            <WithIcon icon={<SendIcon size={16} />}>Publish</WithIcon>
          </button>
        </div>
      </div>

      {publish.error ? <ErrorBox error={publish.error} /> : null}
      {setAvailability.error ? <ErrorBox error={setAvailability.error} /> : null}
      {publish.data ? (
        <div className="notice">
          <WithIcon icon={<CheckCircleIcon size={16} />}>
            Version {publish.data.version} is live · {publish.data.liveItems} items
          </WithIcon>
        </div>
      ) : null}

      {menu.items.length === 0 ? (
        <p className="muted empty-state">
          <WithIcon icon={<MenuIcon size={20} />}>No menu yet</WithIcon>
        </p>
      ) : grouped.length === 0 ? (
        <p className="muted empty-state">
          <WithIcon icon={<SearchIcon size={20} />}>No dishes match “{filter}”</WithIcon>
        </p>
      ) : (
        grouped.map((section) => (
          <section key={section.id} className="menu-section">
            <h2 className="section-title">
              {section.label} <span className="count">{section.items.length}</span>
            </h2>
            <ul className="items">
              {section.items.map((item) => (
                <li key={item.itemId} className={item.available ? "item" : "item soldout"}>
                  <button className="item-main" onClick={() => setEditing(item)}>
                    <span className="item-name">{displayName(item.name)}</span>
                    {item.description ? (
                      <span className="item-desc">{item.description}</span>
                    ) : null}
                  </button>
                  <span className="item-price">{formatMoney(item.basePrice)}</span>
                  <button
                    className={item.available ? "ghost" : "warn"}
                    disabled={
                      setAvailability.isPending && setAvailability.variables?.itemId === item.itemId
                    }
                    onClick={() =>
                      setAvailability.mutate({ itemId: item.itemId, available: !item.available })
                    }
                  >
                    {item.available ? (
                      <WithIcon icon={<BanIcon size={15} />}>Mark sold out</WithIcon>
                    ) : (
                      <WithIcon icon={<CheckIcon size={15} />}>Restore</WithIcon>
                    )}
                  </button>
                  <button
                    className="ghost icon-button"
                    onClick={() => setEditing(item)}
                    aria-label={`Edit ${displayName(item.name)}`}
                    title="Edit"
                  >
                    <PencilIcon size={16} />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}

      {confirmingPublish && (
        <PublishDialog
          restaurantId={restaurantId}
          publishing={publish.isPending}
          error={publish.error}
          onCancel={() => {
            publish.reset();
            setConfirmingPublish(false);
          }}
          onConfirm={() => publish.mutate()}
        />
      )}

      {editing && (
        <ItemEditor
          {...(editing === "new" ? {} : { item: editing })}
          categories={menu.categories}
          groups={menu.modifierGroups}
          saving={saveItem.isPending || addItem.isPending || removeItem.isPending}
          error={firstError(saveItem.error, addItem.error, removeItem.error)}
          onCancel={() => {
            saveItem.reset();
            addItem.reset();
            removeItem.reset();
            setEditing(null);
          }}
          onSubmit={(form) => {
            if (editing === "new") addItem.mutate(toNewItem(form));
            else saveItem.mutate(toUpdatedItem(form, editing));
          }}
          {...(editing === "new"
            ? {}
            : {
                onDelete: () => {
                  // Deleting a dish can't be undone from here.
                  if (window.confirm(`Delete “${editing.name.en}” from the menu?`)) {
                    removeItem.mutate(editing.itemId);
                  }
                }
              })}
        />
      )}
    </>
  );
}

/** First mutation error, so the modal shows whichever action actually failed. */
function firstError(...errors: unknown[]): string | undefined {
  const e = errors.find(Boolean);
  return e instanceof Error ? e.message : undefined;
}

export function ErrorBox({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  const problems = error instanceof ApiError ? error.problems : [];
  return (
    <div className="error">
      {message}
      {problems.length > 0 && (
        <ul>
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
